import React, { useContext, useMemo } from 'react';
import { Button, Col, Flex, Row } from 'antd';
import dayjs from 'dayjs';

import OptimizeEditButton from '@totem/components/ticketing/details/optimize/optimizeEditButton';
import OptimizeRoiDisplay from '@totem/components/ticketing/details/optimize/optimizeRoiDisplay';
import OptimizeSavingsDisplay from '@totem/components/ticketing/details/optimize/optimizeSavingsDisplay';
import OptimizeStatus from '@totem/components/ticketing/details/optimize/optimizeStatus';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { StatusHistory } from '@totem/components/ticketing/optimizeTypes';
import { Activity } from '@totem/components/ticketing/ticketing';
import { defaultActivity } from '@totem/components/ticketing/utilities';
import HeaderItem from '@totem/components/ui_components/headers/HeaderItem';
import Currency from '@totem/components/ui_components/widgets/currency/Currency';
import TagDisplay, {
  LEVEL_OF_EFFORT_STYLES,
  SCOPE_OF_RETURN_STYLES,
} from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';

const OptimizePanel = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket, onOptimizeStatusChange, onCreateActivity } =
    useContext(TicketContext);

  const isInternal = useMemo(() => {
    return permissions.internal.hasPermissions(userProfile);
  }, [userProfile]);

  const hasOptimize =
    isNotNull(ticket) &&
    isNotNull(ticket.ticket.extras) &&
    isNotNull(ticket.ticket.extras.optimize);

  return (
    <div>
      <Row>
        <Col span={6}>
          <HeaderItem title="Savings Type">
            {hasOptimize &&
            isNotNull(ticket.ticket.extras.optimize.savingsType) ? (
              <span>{ticket.ticket.extras.optimize.savingsType}</span>
            ) : (
              ''
            )}
          </HeaderItem>
          <HeaderItem title="Savings Potential">
            {hasOptimize &&
            isNotNull(ticket.ticket.extras.optimize.savingsPotential) ? (
              <OptimizeSavingsDisplay
                optimize={ticket.ticket.extras.optimize}
              />
            ) : (
              ''
            )}
          </HeaderItem>
          <HeaderItem title="Required Investment">
            {hasOptimize &&
            isNotNull(ticket.ticket.extras.optimize.requiredInvestment) ? (
              <span style={{ textAlign: 'left', display: 'block' }}>
                <Currency
                  amount={ticket.ticket.extras.optimize.requiredInvestment}
                />
              </span>
            ) : (
              ''
            )}
          </HeaderItem>
          <HeaderItem title="Return on Investment">
            {hasOptimize && (
              <OptimizeRoiDisplay optimize={ticket.ticket.extras.optimize} />
            )}
          </HeaderItem>
        </Col>
        <Col span={6}>
          <HeaderItem title="Status">
            {hasOptimize && (
              <Flex justify={'flex-start'} align={'center'}>
                <OptimizeStatus
                  status={
                    isNotNull(
                      ticket.ticket.extras.optimize.opportunityStatus,
                    ) && ticket.ticket.extras.optimize.opportunityStatus !== ''
                      ? ticket.ticket.extras.optimize.opportunityStatus
                      : 'Not Set'
                  }
                />
                {isInternal &&
                  ticket.ticket.extras.optimize.opportunityStatus ===
                    'Rejected' && (
                    <Flex justify={'flex-start'} align={'center'}>
                      <Button
                        size={'small'}
                        style={{ marginLeft: '8px' }}
                        onClick={() => {
                          const update: StatusHistory = {
                            fromStatus:
                              ticket.ticket.extras.optimize.opportunityStatus,
                            toStatus: 'Draft',
                            changedBy: '',
                            changedAt: dayjs().toISOString(),
                            notes: 'Rework after rejected',
                          };
                          onOptimizeStatusChange(update);
                        }}
                      >
                        Rework
                      </Button>
                      <Button
                        size={'small'}
                        style={{ marginLeft: '8px' }}
                        onClick={() => {
                          const update: StatusHistory = {
                            fromStatus:
                              ticket.ticket.extras.optimize.opportunityStatus,
                            toStatus: 'Cancelled',
                            changedBy: '',
                            changedAt: dayjs().toISOString(),
                            notes: 'Opportunity Cancelled/Rejected',
                          };
                          onOptimizeStatusChange(update);

                          const activity: Activity = {
                            ...defaultActivity,
                            type: {
                              id: '6a21af688129edee2d0db8fa',
                              code: 'CANCEL_REJECT',
                              display: 'Cancel Ticket',
                            },
                            notes: {
                              isRichText: true,
                              plainText: 'Approval Rejected',
                              htmlText: 'Approval Rejected',
                            },
                            createdAt: new Date().toISOString(),
                          };
                          onCreateActivity(activity);
                        }}
                      >
                        Close
                      </Button>
                    </Flex>
                  )}

                {isInternal &&
                  ticket.ticket.extras.optimize.opportunityStatus ===
                    'Approved' && (
                    <Flex justify={'flex-start'} align={'center'}>
                      <Button
                        size={'small'}
                        style={{ marginLeft: '8px' }}
                        onClick={() => {
                          const update: StatusHistory = {
                            fromStatus:
                              ticket.ticket.extras.optimize.opportunityStatus,
                            toStatus: 'In Progress',
                            changedBy: '',
                            changedAt: dayjs().toISOString(),
                            notes: 'Project Approved',
                          };
                          onOptimizeStatusChange(update);
                        }}
                      >
                        In Progress
                      </Button>
                    </Flex>
                  )}

                {isInternal &&
                  ticket.ticket.extras.optimize.opportunityStatus ===
                    'In Progress' && (
                    <Flex justify={'flex-start'} align={'center'}>
                      <Button
                        size={'small'}
                        style={{ marginLeft: '8px' }}
                        onClick={() => {
                          const update: StatusHistory = {
                            fromStatus:
                              ticket.ticket.extras.optimize.opportunityStatus,
                            toStatus: 'Verifying',
                            changedBy: '',
                            changedAt: dayjs().toISOString(),
                            notes: 'Work Completed',
                          };
                          onOptimizeStatusChange(update);
                        }}
                      >
                        Verifying
                      </Button>
                    </Flex>
                  )}
              </Flex>
            )}
          </HeaderItem>
          <HeaderItem title="Category">
            {hasOptimize && isNotNull(ticket.ticket.extras.optimize.category)
              ? ticket.ticket.extras.optimize.category
              : ''}
          </HeaderItem>
          <HeaderItem title="Level of Effort">
            {hasOptimize && (
              <TagDisplay
                value={
                  isNotNull(ticket.ticket.extras.optimize.levelOfEffort)
                    ? ticket.ticket.extras.optimize.levelOfEffort
                    : ''
                }
                config={LEVEL_OF_EFFORT_STYLES}
              />
            )}
          </HeaderItem>
          <HeaderItem title="Scope of Return">
            {hasOptimize && (
              <TagDisplay
                value={
                  isNotNull(ticket.ticket.extras.optimize.scopeOfReturn)
                    ? ticket.ticket.extras.optimize.scopeOfReturn
                    : ''
                }
                config={SCOPE_OF_RETURN_STYLES}
              />
            )}
          </HeaderItem>
        </Col>
        <Col span={10}>
          <HeaderItem title="Steps Required">
            {hasOptimize &&
            isNotNull(ticket.ticket.extras.optimize.stepsRequired)
              ? ticket.ticket.extras.optimize.stepsRequired
              : ''}
          </HeaderItem>
        </Col>
        <Col span={2}>
          <Flex justify="flex-end" align="flex-start">
            <OptimizeEditButton />
          </Flex>
        </Col>
      </Row>
    </div>
  );
};

export default OptimizePanel;
